// FIGYELEM-SÁV — inline (nem-modális) figyelmeztető panel egy lista-oldal ELÉ (a toolbar fölé), amikor a
// szerver adata azt mondja, hogy valamit ellenőrizni kell (pl. hamarosan induló, még függő hozzárendelés).
//
// PROMÓCIÓ (2. használat, `apps/web/CLAUDE.md` szabálya): a mintát a hozzárendelés-szerkesztő sheet
// „számlázott, zárolt" figyelmeztetése hordozta elsőként (`service-assignment-form-sheet.tsx`,
// `border-warning/40 bg-warning-soft text-warning rounded-lg border p-3 text-xs`). A hozzárendelés-lista
// figyelem-panelje a 2. hívó — a szabály szerint a minta ide, `components/shared/`-be emelendő, ÁLTALÁNOSÍTOTT
// névvel és két tónussal (a `danger` a jövőbeli szigorúbb esetekhez).
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A sáv TÓNUSA = a mondanivaló súlya. `warning`: tennivaló vagy alulbecslés (borostyán) · `danger`: hiba /
 * visszafordíthatatlan következmény · `neutral`: MELLÉKES állapot-információ, amire nem kell reagálni
 * (semleges felület, info-ikon) — a neutrális sáv is IDE tartozik, nem kézzel írt `bg-sunken` div-be.
 */
export type AttentionBannerTone = 'warning' | 'danger' | 'neutral';

export interface AttentionBannerProps {
  tone: AttentionBannerTone;
  /** Vezető ikon — alap: a tónusé (figyelmeztetésen háromszög, semlegesen „i"); a hívó felülírhatja. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Jobbra igazított akció (pl. „Megnézem" gomb egy részletező dialógushoz). */
  action?: ReactNode;
  className?: string;
}

const TONE_CLASSES: Record<AttentionBannerTone, string> = {
  warning: 'border-warning/40 bg-warning-soft text-warning',
  danger: 'border-danger/40 bg-danger-soft text-danger',
  neutral: 'border-border/60 bg-sunken/40 text-muted-foreground',
};

/** A vezető jel a TÓNUSBÓL jön: figyelmeztetésen háromszög, semleges tájékoztatáson „i" — a hívó felülírhatja. */
const TONE_ICONS: Record<AttentionBannerTone, JSX.Element> = {
  warning: <ExclamationTriangleIcon className="size-4" />,
  danger: <ExclamationTriangleIcon className="size-4" />,
  neutral: <InformationCircleIcon className="size-4" />,
};

/** Inline figyelmeztető sáv — form-belüli jelzésre ÉS lista-oldal fölötti panelre egyaránt (lásd a fenti promóció-jegyzetet). */
export function AttentionBanner({
  tone,
  icon,
  title,
  description,
  action,
  className,
}: AttentionBannerProps): JSX.Element {
  return (
    <div
      role="status"
      className={cn(
        'flex gap-3 rounded-lg border p-3 text-xs',
        // EGYSOROS sávon MINDEN függőlegesen középen (az ikon, a szöveg ÉS az akció-gomb is) — a gomb
        // magasabb a szövegnél, tehát `items-start` mellett a felirat a gomb tetejéhez tapadt, nem a
        // közepéhez. TÖBBSOROS leírásnál marad a felső igazítás: ott a középre húzott ikon lógna el a
        // szöveg-blokk mellett.
        description === undefined ? 'items-center' : 'items-start',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <span className={cn('shrink-0', description !== undefined && 'mt-0.5')}>
        {icon ?? TONE_ICONS[tone]}
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium">{title}</div>
        {description && <div className="opacity-90">{description}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
